"use client"

import { buildImageUrl } from "@/sanity/lib/image"
import type { SanityImageSource } from "@sanity/image-url/lib/types/types"
import Image, { ImageProps } from "next/image"
import { useEffect, useState } from "react"
import { sanityBaseUrl } from "@/lib/sanityLoader"

interface BaseImageProps extends Omit<ImageProps, "src"> {
    src: SanityImageSource | string
    quality?: number
    mimeType?: string
}

export default function BaseImage({
    quality = 80,
    src,
    mimeType,
    ...props
}: BaseImageProps) {
    const [isLoaded, setIsLoaded] = useState(false)
    const initialWidth = 100

    useEffect(() => {
        setIsLoaded(true)
    }, [])

    const isSanityImage =
        (typeof src === "string" && src.startsWith(sanityBaseUrl)) ||
        (typeof src === "string" && src.startsWith("image-"))

    const previewLoader = ({ src }: { src: string }): string => {
        if (isSanityImage) {
            let imageUrl =
                buildImageUrl(src)
                    .width(initialWidth)
                    .auto("format")
                    .quality(quality)
                    .fit("clip")
                    .url() ?? ""

            if (mimeType === "image/gif") {
                imageUrl += `&h=${Math.round(initialWidth / 3)}`
            }
            return imageUrl
        }
        return src
    }

    return (
        <>
            <Image
                {...props}
                unoptimized={!isSanityImage} // only sanity images are optimizeable
                alt={props.alt || ""}
                src={typeof src === "string" ? src : ""}
                onLoad={() => setIsLoaded(true)}
            />{" "}
            <Image
                {...props}
                unoptimized={!isSanityImage} // only sanity images are optimizeable
                alt={props.alt || ""}
                loader={previewLoader}
                src={src as string}
                sizes="33vw" // have all the lazyloaded images to be sized to 33vw
                id="preview"
                className={`${isLoaded ? "opacity-0" : "opacity-100"} absolute left-0 top-0 transition-opacity duration-100`}
            />
        </>
    )
}
